import { FC, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { camelCase, snakeCase } from 'lodash';

import { Slider } from '@phoenix/design-system/frontend/ui/slider/slider';
import { EditorialCard } from '@phoenix/core/frontend/shared/ui/editorial-card/editorial-card';
import { LinkOut } from '@phoenix/core/frontend/shared/ui/link-out/link-out';

import { VideoChannel } from '@phoenix/team-m/dashboard/frontend/util/video-channel/video-channel.model';
import { DynamicLinkWrapper } from '@phoenix/team-m/dashboard/frontend/shared/dynamic-link-wrapper';
import { useEditorialCardSliderOptions } from '@phoenix/team-m/dashboard/frontend/shared/use-editorial-card-slider-options';
import { SharedHint } from '@phoenix/team-m/dashboard/frontend/shared/components/shared-hint';

import { VideosChannelLoadingState } from './videos-channel-loading-state';

interface VideosChannelProps {
  title: string;
  isLoading?: boolean;
  channel: VideoChannel;
  dataTestId?: string;
}

export const VideosChannel: FC<VideosChannelProps> = ({ title, isLoading = false, channel, dataTestId = 'videoChannel' }) => {
  const { t } = useTranslation();
  const sliderOptions = useEditorialCardSliderOptions();
  const { current: activeSlideForChannel } = useRef<Record<string, number>>({});
  const id = `videoChannel_${camelCase(title)}`;
  const { url, videos, isFallback } = channel;

  if (isLoading) {
    return <VideosChannelLoadingState />;
  }

  return (
    <>
      {url && (
        <div className="right-0 top-10 mt-4 desktop:absolute desktop:mt-0" data-testid="visitChannelBtn">
          <LinkOut variant="neutral" href={url} allowReferrer={true}>
            {t('dashboard_youtube_button_visitYouTube')}
          </LinkOut>
        </div>
      )}
      {isFallback && <SharedHint className="mt-4" hint={t('dashboard_youtube_international_hint')} />}
      <Slider
        id="videos-channel"
        className="mt-6 !p-1"
        sliderOptions={sliderOptions}
        onSlideChangeTransitionEnd={(swiper) => (activeSlideForChannel[id] = swiper.activeIndex)}
        onUpdate={(swiper) => swiper.slideTo(activeSlideForChannel[id] ?? 0)}
        dataTestId={dataTestId}
        slidesOffsetAfter={3}
      >
        {videos.map((video, index: number) => (
          <DynamicLinkWrapper
            aria-label={`${video.title} (${t('shared:shared_a11y_hint_opensInNewTab')})`}
            key={`${snakeCase(video.title)}`}
            hyperLink={video.url}
            className="flex h-full"
            allowReferrer={true}
          >
            <EditorialCard
              image={{
                src: video.thumbnailUrl,
                alt: video?.description,
              }}
              title={video.title}
              additionalInfo={[video.publishedAt]}
              dataTestId={`videoCard_${index + 1}`}
              linkOutText={t('dashboard_youtube_card_viewVideo')}
            />
          </DynamicLinkWrapper>
        ))}
      </Slider>
    </>
  );
};
